import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ScrollText, Sparkles, Download, Printer, Trash2, Check, Mail } from "lucide-react";
import { api } from "../api";
import { PageHeader, GlassCard, Empty, Spinner, Disclaimer, Chip } from "../components/ui";

const BUREAUS = ["Experian", "Equifax", "TransUnion"];
const EMPTY = { bureau: "Experian", account_name: "", account_type: "Collection", reason: "" };

export default function Disputes() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [letter, setLetter] = useState("");
  const [history, setHistory] = useState(null);

  const load = () => api.get("/disputes").then(({ data }) => setHistory(data));
  useEffect(() => { load(); }, []);

  const generate = async (e) => {
    e.preventDefault();
    if (!form.account_name.trim() || !form.reason.trim()) return toast.error("Account name and reason are required");
    setLoading(true);
    try {
      const { data } = await api.post("/disputes/generate", form);
      setActive(data); setLetter(data.letter); setForm(EMPTY); load();
      toast.success("Dispute letter generated");
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setLoading(false); }
  };

  const openItem = (d) => { setActive(d); setLetter(d.letter); };

  const download = (ext) => {
    if (!active) return;
    const blob = new Blob([letter], { type: ext === "doc" ? "application/msword" : "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `dispute-${active.account_name}.${ext === "doc" ? "doc" : "txt"}`;
    a.click(); URL.revokeObjectURL(url);
  };
  const print = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const pre = w.document.createElement("pre");
    pre.style.cssText = "font-family:Georgia,serif;white-space:pre-wrap;padding:40px;line-height:1.6";
    pre.textContent = letter;
    w.document.body.appendChild(pre);
    w.print();
  };
  const remove = async (id) => {
    setHistory((h) => h.filter((x) => x.id !== id));
    if (active?.id === id) { setActive(null); setLetter(""); }
    try { await api.delete(`/disputes/${id}`); } catch { load(); }
  };

  if (history === null) return <Spinner />;

  return (
    <div className="space-y-6">
      <PageHeader label="AI Tool" title="AI Dispute Assistant"
        subtitle="Generate professional dispute letter templates with supporting document checklists." />

      <div className="grid lg:grid-cols-2 gap-5">
        <GlassCard className="p-6">
          <h3 className="cc-heading text-lg font-500 text-white mb-4">New Dispute</h3>
          <form onSubmit={generate} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <select value={form.bureau} onChange={(e) => setForm({ ...form, bureau: e.target.value })} className="cc-input rounded-xl px-3 py-2.5 text-sm" data-testid="dispute-bureau">
                {BUREAUS.map((b) => <option key={b} className="bg-[#1a1a24]">{b}</option>)}
              </select>
              <input value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })} placeholder="Account type" className="cc-input rounded-xl px-3 py-2.5 text-sm" />
            </div>
            <input value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} placeholder="Account / creditor name" className="cc-input w-full rounded-xl px-4 py-2.5 text-sm" data-testid="dispute-account" />
            <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={4} placeholder="Reason for dispute (e.g. this account is not mine, incorrect balance, already paid)…" className="cc-input w-full rounded-xl px-4 py-2.5 text-sm" data-testid="dispute-reason" />
            <button type="submit" disabled={loading} className="cc-btn-primary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-500 disabled:opacity-60" data-testid="dispute-generate">
              <Sparkles size={16} /> {loading ? "Generating…" : "Generate Letter"}
            </button>
          </form>
        </GlassCard>

        <GlassCard className="p-6">
          {active ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <h3 className="cc-heading text-lg font-500 text-white">Letter — {active.account_name}</h3>
                <div className="flex gap-2">
                  <button onClick={() => download("txt")} className="text-slate-400 hover:text-white p-1.5" title="Download .txt" data-testid="dispute-download-txt"><Download size={16} /></button>
                  <button onClick={() => download("doc")} className="text-slate-400 hover:text-white p-1.5" title="Download .doc"><FileTag /></button>
                  <button onClick={print} className="text-slate-400 hover:text-white p-1.5" title="Print" data-testid="dispute-print"><Printer size={16} /></button>
                  <button onClick={async () => { try { await api.post(`/disputes/${active.id}/email`); toast.success("Letter emailed to you"); } catch { toast.error("Could not email letter"); } }} className="text-slate-400 hover:text-white p-1.5" title="Email to me" data-testid="dispute-email"><Mail size={16} /></button>
                </div>
              </div>
              <textarea value={letter} onChange={(e) => setLetter(e.target.value)} rows={12} className="cc-input w-full rounded-xl px-4 py-3 text-sm font-mono leading-relaxed" data-testid="dispute-letter" />
              <div className="mt-4">
                <div className="text-xs uppercase tracking-widest text-blue-300 mb-2">Supporting documents</div>
                <div className="space-y-1.5">
                  {active.checklist?.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-slate-300"><Check size={14} className="text-blue-400" /> {c}</div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <Empty icon={ScrollText} title="No letter selected" subtitle="Generate a new dispute letter or select one from your history." />
          )}
        </GlassCard>
      </div>

      <Disclaimer text="AI-generated documents are educational templates only and should be reviewed before sending." />

      <GlassCard className="p-6">
        <h3 className="cc-heading text-base font-500 text-white mb-4">Dispute History</h3>
        {history.length === 0 ? (
          <p className="text-sm text-slate-500">No disputes yet.</p>
        ) : (
          <div className="space-y-2">
            {history.map((d) => (
              <div key={d.id} className="flex items-center justify-between rounded-lg cc-surface border border-white/[0.05] px-4 py-3">
                <button onClick={() => openItem(d)} className="flex items-center gap-3 text-left flex-1">
                  <span className="text-sm text-slate-200">{d.account_name}</span>
                  <Chip tone="blue">{d.bureau}</Chip>
                  <span className="text-xs text-slate-500">{new Date(d.created_at).toLocaleDateString()}</span>
                </button>
                <button onClick={() => remove(d.id)} className="text-slate-500 hover:text-red-400 p-1"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        )}
      </GlassCard>
    </div>
  );
}

const FileTag = () => <span className="text-xs font-600">DOC</span>;
